import { ModelProviderError } from '../errors'
import { agentReasoningText, normalizeAgentReasoning } from '../messages'
import {
  abortSignal,
  isPlainRecord,
  parseJson,
  parseResponseJson,
  parseServerSentEventLine,
  providerHttpError,
  providerUrl,
  requestHeaders,
} from '../http'
import type {
  AgentMessage,
  AgentToolCall,
  AgentToolDefinition,
  FetchLike,
  ModelCapabilities,
  ModelClient,
  ModelClientOptions,
  ModelEvent,
  ModelProviderConfig,
  ModelRequest,
  ModelResponse,
  ModelUsage,
  OpenAIChatReasoningReplayFormat,
} from '../types'

interface OpenAIChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool'
  content: string | null | Array<
    | { type: 'text'; text: string; cache_control?: { type: 'ephemeral' } }
    | { type: 'image_url'; image_url: { url: string } }
  >
  reasoning?: string
  reasoning_content?: string
  reasoning_details?: unknown
  name?: string
  tool_call_id?: string
  tool_calls?: OpenAIToolCall[]
}

interface OpenAIToolCall {
  id: string
  type: 'function'
  function: {
    name: string
    arguments: string
  }
}

interface OpenAIToolDefinition {
  type: 'function'
  function: {
    name: string
    description?: string
    parameters?: Record<string, unknown>
  }
}

type OpenAIMessageContent = string | Array<{ type?: string; text?: string }> | null | undefined

interface OpenAIChatResponseMessage {
  content?: OpenAIMessageContent
  reasoning?: string
  reasoning_content?: string
  reasoning_details?: unknown
  tool_calls?: OpenAIToolCall[]
}

interface OpenAIChatPayload {
  model: string
  messages: OpenAIChatMessage[]
  temperature?: number
  max_tokens?: number
  tools?: OpenAIToolDefinition[]
  tool_choice?: 'auto' | 'none' | 'required'
  stream?: boolean
  stream_options?: {
    include_usage: boolean
  }
  vl_high_resolution_images?: true
}

interface OpenAIChatResponse {
  id?: string
  model?: string
  choices?: Array<{
    message?: OpenAIChatResponseMessage
    delta?: {
      content?: string | null
      reasoning?: string | null
      reasoning_content?: string | null
      reasoning_details?: unknown
      tool_calls?: Array<{
        index?: number
        id?: string
        type?: 'function'
        function?: {
          name?: string
          arguments?: string
        }
      }>
    }
    finish_reason?: string | null
  }>
  usage?: {
    prompt_tokens?: number
    completion_tokens?: number
    total_tokens?: number
    prompt_tokens_details?: { cached_tokens?: number }
    completion_tokens_details?: { reasoning_tokens?: number }
  } | null
  error?: {
    message?: string
    type?: string
    code?: string
  }
}

const defaultCapabilities: ModelCapabilities = {
  streaming: true,
  tools: true,
  vision: true,
  jsonMode: false,
  systemPrompt: true,
}

export class OpenAICompatibleModelClient implements ModelClient {
  readonly provider: string
  readonly requestStyle = 'openai-chat'
  readonly capabilities: ModelCapabilities

  private readonly config: ModelProviderConfig
  private readonly fetchImpl: FetchLike

  constructor(config: ModelProviderConfig, options: ModelClientOptions = {}) {
    this.config = config
    this.provider = config.id
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis)
    this.capabilities = {
      ...defaultCapabilities,
      ...config.capabilities,
    }
  }

  requestTarget(): string {
    return chatCompletionsUrl(this.config)
  }

  async create(request: ModelRequest): Promise<ModelResponse> {
    const payload = toOpenAIChatPayload(this.config, { ...request, stream: false })
    const response = await this.postJson(payload, request.signal)
    return normalizeOpenAIChatResponse(this.provider, response)
  }

  async *stream(request: ModelRequest): AsyncIterable<ModelEvent> {
    const payload = toOpenAIChatPayload(this.config, { ...request, stream: true })
    const response = await this.post(payload, request.signal)

    if (!response.body) {
      throw new ModelProviderError('Model provider returned an empty stream body.', {
        provider: this.provider,
        statusCode: response.status,
      })
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    const toolCalls = new Map<number, { id: string; name: string; argumentsText: string }>()
    let buffer = ''
    let finishReason: string | undefined
    let responseId: string | undefined
    let responseModel: string | undefined
    const reasoningDetails = new Map<string, Record<string, unknown>>()

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split(/\r?\n/)
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          const event = parseServerSentEventLine(line)
          if (!event) continue
          if (event === '[DONE]') {
            yield {
              type: 'done',
              response: {
                id: responseId,
                model: responseModel,
                finishReason,
                reasoning: normalizeAgentReasoning(
                  undefined,
                  openAIReasoningDetails([...reasoningDetails.values()]),
                ),
              },
            }
            return
          }

          const chunk = parseJson<OpenAIChatResponse>(event)
          if (!chunk) continue
          responseId = chunk.id ?? responseId
          responseModel = chunk.model ?? responseModel

          if (chunk.usage) {
            yield { type: 'usage', usage: normalizeUsage(chunk.usage) }
          }

          const choice = chunk.choices?.[0]
          if (!choice) continue
          finishReason = choice.finish_reason ?? finishReason

          const content = choice.delta?.content
          if (content) {
            yield { type: 'text-delta', text: content }
          }
          const reasoning = choice.delta?.reasoning_content ?? choice.delta?.reasoning
          if (reasoning) {
            yield { type: 'reasoning-delta', text: reasoning }
          } else {
            const detailsText = reasoningDetailsText(choice.delta?.reasoning_details)
            if (detailsText) yield { type: 'reasoning-delta', text: detailsText }
          }
          mergeOpenAIReasoningDetails(reasoningDetails, choice.delta?.reasoning_details)

          for (const toolCallDelta of choice.delta?.tool_calls ?? []) {
            const index = toolCallDelta.index ?? 0
            const current = toolCalls.get(index) ?? { id: '', name: '', argumentsText: '' }
            current.id = toolCallDelta.id ?? current.id
            current.name = toolCallDelta.function?.name ?? current.name
            current.argumentsText += toolCallDelta.function?.arguments ?? ''
            toolCalls.set(index, current)
          }

          if (choice.finish_reason === 'tool_calls') {
            for (const toolCall of toolCalls.values()) {
              yield { type: 'tool-call', toolCall: normalizeToolCall(toolCall.id, toolCall.name, toolCall.argumentsText) }
            }
            toolCalls.clear()
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    yield {
      type: 'done',
      response: {
        id: responseId,
        model: responseModel,
        finishReason,
        reasoning: normalizeAgentReasoning(
          undefined,
          openAIReasoningDetails([...reasoningDetails.values()]),
        ),
      },
    }
  }

  private async postJson(payload: OpenAIChatPayload, signal?: AbortSignal): Promise<OpenAIChatResponse> {
    const response = await this.post(payload, signal)
    return parseResponseJson(this.provider, response)
  }

  private async post(payload: OpenAIChatPayload, signal?: AbortSignal): Promise<Response> {
    const response = await this.fetchImpl(chatCompletionsUrl(this.config), {
      method: 'POST',
      headers: requestHeaders(this.config),
      body: JSON.stringify(payload),
      signal: abortSignal(this.config.timeoutMs, signal),
    })

    if (!response.ok) {
      throw await providerHttpError(this.provider, response)
    }

    return response
  }
}

export function toOpenAIChatPayload(config: ModelProviderConfig, request: ModelRequest): OpenAIChatPayload {
  const isQwenOAuth = config.id === 'qwen-oauth'
  const reasoningReplayField = openAIReasoningReplayField(
    config,
    request.model ?? config.defaultModel,
  )
  const requiresAssistantContent = requiresNonNullAssistantContent(
    config,
    request.model ?? config.defaultModel,
  )
  const tools = request.tools?.length ? request.tools.map(toOpenAIToolDefinition) : undefined
  return {
    model: request.model ?? config.defaultModel,
    messages: request.messages.flatMap(message =>
      toOpenAIChatMessages(message, isQwenOAuth, reasoningReplayField, requiresAssistantContent)),
    temperature: request.temperature,
    max_tokens: request.maxTokens,
    ...(tools
      ? {
          tools,
          ...(request.toolChoice ? { tool_choice: request.toolChoice } : {}),
        }
      : {}),
    stream: request.stream,
    stream_options: request.stream ? { include_usage: true } : undefined,
    vl_high_resolution_images: isQwenOAuth ? true : undefined,
  }
}

export function normalizeOpenAIChatResponse(provider: string, response: OpenAIChatResponse): ModelResponse {
  if (response.error) {
    throw new ModelProviderError(response.error.message ?? 'Model provider returned an error.', {
      provider,
      details: response.error,
    })
  }

  const choice = response.choices?.[0]
  return {
    id: response.id,
    model: response.model,
    content: normalizeContent(choice?.message?.content),
    reasoning: normalizeAgentReasoning(
      normalizeReasoning(choice?.message),
      openAIReasoningDetails(choice?.message?.reasoning_details),
    ),
    toolCalls: choice?.message?.tool_calls?.map(toAgentToolCall),
    usage: response.usage ? normalizeUsage(response.usage) : undefined,
    finishReason: choice?.finish_reason ?? undefined,
    raw: response,
  }
}

function normalizeReasoning(message: OpenAIChatResponseMessage | undefined): string | undefined {
  if (!message) return undefined
  if (typeof message.reasoning_content === 'string' && message.reasoning_content) return message.reasoning_content
  if (typeof message.reasoning === 'string' && message.reasoning) return message.reasoning
  return reasoningDetailsText(message.reasoning_details)
}

function toOpenAIChatMessages(
  message: AgentMessage,
  qwenOAuth = false,
  reasoningReplayField?: OpenAIChatReasoningReplayFormat,
  requiresAssistantContent = false,
): OpenAIChatMessage[] {
  const plainContent = message.role === 'assistant' && message.toolCalls?.length
    ? message.content || (requiresAssistantContent ? '' : null)
    : message.content
  const content = qwenOAuth && typeof plainContent === 'string'
    ? [{
        type: 'text' as const,
        text: plainContent,
        ...(message.role === 'system' ? { cache_control: { type: 'ephemeral' as const } } : {}),
      }]
    : plainContent
  const reasoningText = agentReasoningText(message.reasoning)
  const reasoningDetails = openAIReasoningDetailsData(message)
  const reasoningReplay: Partial<OpenAIChatMessage> = message.role !== 'assistant'
    ? {}
    : reasoningReplayField === 'reasoning' && reasoningText
      ? { reasoning: reasoningText }
      : reasoningReplayField === 'reasoning_content' && reasoningText
        ? { reasoning_content: reasoningText }
        : reasoningReplayField === 'reasoning_details' && reasoningDetails
          ? { reasoning_details: reasoningDetails }
          : reasoningReplayField === 'reasoning_details' && reasoningText
            ? { reasoning: reasoningText }
            : {}
  const base: OpenAIChatMessage = {
    role: message.role,
    content,
    ...reasoningReplay,
    name: message.name,
    tool_call_id: message.toolCallId,
    tool_calls: message.toolCalls?.map(toOpenAIToolCall),
  }
  const images = message.contentParts?.filter(part => part.type === 'image') ?? []
  if (message.role === 'user' && images.length > 0) {
    return [{
      ...base,
      content: [
        ...(message.content ? [{ type: 'text' as const, text: message.content }] : []),
        ...images.map(image => ({ type: 'image_url' as const, image_url: { url: `data:${image.mimeType};base64,${image.data}` } })),
      ],
    }]
  }
  if (message.role !== 'tool' || images.length === 0) return [base]
  return [base, {
    role: 'user',
    content: [
      { type: 'text', text: 'Visual output from the preceding tool result:' },
      ...images.map(image => ({ type: 'image_url' as const, image_url: { url: `data:${image.mimeType};base64,${image.data}` } })),
    ],
  }]
}

function openAIReasoningReplayField(
  config: ModelProviderConfig,
  model: string,
): OpenAIChatReasoningReplayFormat {
  if (config.openAIChatReasoningReplayFormat) {
    return config.openAIChatReasoningReplayFormat
  }
  const identifier = `${config.id} ${config.baseUrl || ''} ${model}`.toLowerCase()
  // OpenRouter preserves provider-native signatures in `reasoning_details`.
  // Several Chat Completions dialects use `reasoning_content`; the remaining
  // OpenAI-compatible Chat adapters receive the common `reasoning` field.
  if (identifier.includes('openrouter')) return 'reasoning_details'
  if (usesReasoningContentProtocol(identifier)) return 'reasoning_content'
  return 'reasoning'
}

function requiresNonNullAssistantContent(
  config: ModelProviderConfig,
  model: string,
): boolean {
  return openAIReasoningReplayField(config, model) === 'reasoning_content'
}

function usesReasoningContentProtocol(identifier: string): boolean {
  return [
    'deepseek',
    'moonshot',
    'kimi',
    'mimo',
    'xiaomimimo',
    'qwen',
    'qwq',
    'dashscope',
    'glm',
    'z.ai',
    'bigmodel',
  ].some(part => identifier.includes(part))
}

function openAIReasoningDetailsData(message: AgentMessage): unknown {
  return message.reasoning?.native?.format === 'openai-reasoning-details'
    ? message.reasoning.native.data
    : undefined
}

function openAIReasoningDetails(value: unknown) {
  return Array.isArray(value) && value.length
    ? {
        format: 'openai-reasoning-details' as const,
        data: value,
      }
    : undefined
}

function reasoningDetailsText(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined
  const text = value
    .flatMap((detail) => {
      if (!isPlainRecord(detail)) return []
      const summary = Array.isArray(detail.summary)
        ? detail.summary
            .filter(isPlainRecord)
            .map(part => typeof part.text === 'string' ? part.text : '')
        : typeof detail.summary === 'string'
          ? [detail.summary]
          : []
      return [
        typeof detail.text === 'string' ? detail.text : '',
        ...summary,
      ]
    })
    .filter(Boolean)
    .join('\n')
  return text || undefined
}

function mergeOpenAIReasoningDetails(
  target: Map<string, Record<string, unknown>>,
  value: unknown,
): void {
  if (!Array.isArray(value)) return
  for (let position = 0; position < value.length; position += 1) {
    const detail = value[position]
    if (!isPlainRecord(detail)) continue
    const key = [
      String(detail.type || 'detail'),
      String(detail.index ?? detail.id ?? position),
    ].join(':')
    const current = target.get(key) ?? {}
    const merged: Record<string, unknown> = { ...current, ...detail }
    for (const field of ['text', 'data', 'signature']) {
      const previous = typeof current[field] === 'string' ? current[field] : ''
      const next = typeof detail[field] === 'string' ? detail[field] : ''
      if (previous && next && next !== previous) {
        merged[field] = next.startsWith(previous) ? next : `${previous}${next}`
      }
    }
    target.set(key, merged)
  }
}

function toOpenAIToolDefinition(tool: AgentToolDefinition): OpenAIToolDefinition {
  return {
    type: 'function',
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    },
  }
}

function toOpenAIToolCall(toolCall: AgentToolCall): OpenAIToolCall {
  return {
    id: toolCall.id,
    type: 'function',
    function: {
      name: toolCall.name,
      arguments: JSON.stringify(toolCall.arguments),
    },
  }
}

function toAgentToolCall(toolCall: OpenAIToolCall): AgentToolCall {
  return normalizeToolCall(toolCall.id, toolCall.function.name, toolCall.function.arguments)
}

function normalizeToolCall(id: string, name: string, argumentsText: string): AgentToolCall {
  const parsedArguments = parseJson<unknown>(argumentsText)
  if (isPlainRecord(parsedArguments)) {
    return { id, name, arguments: parsedArguments, rawArguments: argumentsText }
  }
  return { id, name, arguments: {}, rawArguments: argumentsText }
}

function normalizeContent(content: OpenAIMessageContent): string {
  if (!content) return ''
  if (typeof content === 'string') return content
  return content.map(part => part.text ?? '').join('')
}

function normalizeUsage(usage: NonNullable<OpenAIChatResponse['usage']>): ModelUsage {
  const cacheReadTokens = usage.prompt_tokens_details?.cached_tokens ?? 0
  const inputTokens = usage.prompt_tokens ?? 0
  return {
    inputTokens: Math.max(0, inputTokens - cacheReadTokens),
    outputTokens: usage.completion_tokens,
    totalTokens: usage.total_tokens,
    cacheReadTokens,
    reasoningTokens: usage.completion_tokens_details?.reasoning_tokens,
  }
}

function chatCompletionsUrl(config: ModelProviderConfig): string {
  return providerUrl(config, 'https://api.openai.com/v1', 'chat/completions')
}
